import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router';

import { EnteredTenantBanner } from '../features/platform/EnteredTenantBanner';
import { useFocusTrap } from '../lib/useFocusTrap';
import { CommandPalette } from './CommandPalette';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

const SIDEBAR_KEY = 'vms.sidebar.collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === '1';
  } catch {
    return false;
  }
}

export function AppShell() {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Below md the menu is a drawer over the page, opened from the top bar.
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleSidebar = useCallback(() => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(SIDEBAR_KEY, c ? '0' : '1');
      } catch {
        /* storage unavailable: preference is per-session only */
      }
      return !c;
    });
  }, []);
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <div className="flex h-dvh overflow-hidden">
      <a
        href="#main"
        className="sr-only z-50 rounded-md bg-accent-strong px-3 py-2 text-white focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
      >
        Pular para o conteúdo
      </a>
      <div className="hidden md:flex">
        <Sidebar collapsed={collapsed} onToggle={toggleSidebar} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <EnteredTenantBanner />
        <Topbar
          menuOpen={menuOpen}
          onOpenMenu={() => {
            setMenuOpen(true);
          }}
          onOpenCommand={() => {
            setPaletteOpen(true);
          }}
        />
        <main
          id="main"
          tabIndex={-1}
          className="relative flex-1 overflow-y-auto focus:outline-none"
        >
          <Outlet />
        </main>
      </div>
      {menuOpen && <MobileMenu onClose={closeMenu} />}
      <CommandPalette
        open={paletteOpen}
        onClose={() => {
          setPaletteOpen(false);
        }}
      />
    </div>
  );
}

/** The navigation as a drawer from the left, on phones. */
function MobileMenu({ onClose }: { onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, onClose);
  return (
    <div className="fixed inset-0 z-40 md:hidden">
      <div aria-hidden="true" className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        ref={ref}
        id="mobile-menu"
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        tabIndex={-1}
        className="relative h-full w-72 max-w-[85vw] shadow-2xl shadow-black/50 focus:outline-none"
      >
        <Sidebar mobile collapsed={false} onToggle={onClose} onNavigate={onClose} />
      </div>
    </div>
  );
}
